import React, { useCallback, useEffect, useRef } from 'react';
import { cn } from './cn';
import type { SafeInputType } from '@crab/frontend-ui';

const BASE_CLASSES =
  'w-full px-3 py-3 md:py-2.5 text-base md:text-sm bg-white border border-slate-200 rounded-xl ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 ' +
  'transition-colors duration-150 placeholder:text-slate-400';

// ── Coarse pointer (touch) detection ──────────────────────────────────
//
// 触屏设备(手机/平板裸触屏)= pointer: coarse。桌面 + 平板加键盘/触控板
// = pointer: fine。这是 W3C Media Queries Level 4 检测 primary input
// mechanism 的标准方式,比 viewport 宽度更准确。
export function isCoarsePointer(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

// ── Auto scroll on focus (mobile keyboard UX) ─────────────────────────
//
// iOS/Android 软键盘弹出时,focused field 常被键盘或 sticky footer 遮挡。
// 在 coarse pointer(触屏) 上,focus 延迟 180ms (等键盘动画) 再
// scrollIntoView({ block: 'center' }) 把 field 居中到可视区域。
// 桌面(fine pointer) 不触发,避免鼠标 focus 强制滚动干扰。
const AUTO_SCROLL_DELAY_MS = 180;

function useAutoScrollOnFocus() {
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return useCallback((target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
    if (typeof window === 'undefined') return;
    if (!isCoarsePointer()) return;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (document.activeElement === target) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, AUTO_SCROLL_DELAY_MS);
  }, []);
}

// ── autoFocus 触屏拦截 ─────────────────────────────────────────────────
//
// 桌面端 modal 打开自动 focus 第一字段 = 好 UX。触屏(手机/平板裸触屏)
// 上 autoFocus = 立即弹软键盘占半屏遮挡 modal。在 Input/Select/Textarea
// 这一层静默吃掉 autoFocus,所有 24+ 处 caller(features/*/EditForm,
// features/*/Wizard 等)0 行不动。用户主动点输入框才弹键盘。
function suppressAutoFocusOnTouch(autoFocus: boolean | undefined): boolean | undefined {
  if (!autoFocus) return autoFocus;
  return isCoarsePointer() ? false : autoFocus;
}

// ── Input ──────────────────────────────────────────────────────────────

/**
 * B-C5:`type` 收窄成 `SafeInputType` —— **`'number'` 不在其中**。
 * red_coral 从 2026-04-25 就有这条禁令,console 一直没有,于是本仓 console 侧
 * 5 处 `<Input type="number">` 一路绿灯到 2026-08-19。类型定义在
 * `@crab/frontend-ui`,两个 app 共用一份,不再各写各的。
 */
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: SafeInputType;
  icon?: React.ReactNode;
  focusRing?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ icon, focusRing, className, autoFocus, ...rest }, ref) => {
    const autoScroll = useAutoScrollOnFocus();
    const effectiveAutoFocus = suppressAutoFocusOnTouch(autoFocus);
    if (icon) {
      return (
        <div className="relative w-full">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
            {icon}
          </span>
          <input
            ref={ref}
            className={cn(
              BASE_CLASSES,
              'pl-10',
              focusRing,
              className,
            )}
            {...rest}
            autoFocus={effectiveAutoFocus}
            onFocus={(e) => {
              rest.onFocus?.(e);
              autoScroll(e.currentTarget);
            }}
          />
        </div>
      );
    }

    return (
      <input
        ref={ref}
        className={cn(
          BASE_CLASSES,
          focusRing,
          className,
        )}
        {...rest}
        autoFocus={effectiveAutoFocus}
        onFocus={(e) => {
          rest.onFocus?.(e);
          autoScroll(e.currentTarget);
        }}
      />
    );
  },
);

Input.displayName = 'Input';

// ── Select ─────────────────────────────────────────────────────────────

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  focusRing?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ focusRing, className, autoFocus, ...rest }, ref) => {
    const autoScroll = useAutoScrollOnFocus();
    const effectiveAutoFocus = suppressAutoFocusOnTouch(autoFocus);
    return (
      <select
        ref={ref}
        className={cn(
          BASE_CLASSES,
          'appearance-none cursor-pointer',
          focusRing,
          className,
        )}
        {...rest}
        autoFocus={effectiveAutoFocus}
        onFocus={(e) => {
          rest.onFocus?.(e);
          autoScroll(e.currentTarget);
        }}
      />
    );
  },
);

Select.displayName = 'Select';

// ── Textarea ───────────────────────────────────────────────────────────

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { focusRing?: string }>(
  ({ focusRing, className, autoFocus, ...rest }, ref) => {
    const autoScroll = useAutoScrollOnFocus();
    const effectiveAutoFocus = suppressAutoFocusOnTouch(autoFocus);
    return (
      <textarea
        ref={ref}
        className={cn(
          BASE_CLASSES,
          focusRing,
          className,
        )}
        {...rest}
        autoFocus={effectiveAutoFocus}
        onFocus={(e) => {
          rest.onFocus?.(e);
          autoScroll(e.currentTarget);
        }}
      />
    );
  },
);

Textarea.displayName = 'Textarea';
